import { useForm, Link } from "@inertiajs/react"
import { AppLayout } from "@/layouts/AppLayout"
import type { Source } from "@/types/models"

interface Props {
  source?: Source
  errors?: Record<string, string[]>
}

const EXTRACTION_BACKENDS = ["auto", "firecrawl", "claude"] as const
const EXTRACTION_MODES = ["schema", "prompt"] as const

const DEFAULT_SCHEMA = {
  type: "object",
  properties: {
    items: {
      type: "array",
      items: {
        type: "object",
        properties: {
          title: { type: "string" },
          url: { type: "string" },
          published_at: { type: ["string", "null"] },
        },
        required: ["title", "url"],
      },
    },
  },
  required: ["items"],
}

export default function SourceForm({ source, errors = {} }: Props) {
  const isEdit = !!source?.id
  const title = isEdit ? "Edit Source" : "New Source"

  const { data, setData, post, put, processing } = useForm({
    name: source?.name || "",
    latest_url: source?.latestUrl || "",
    host: source?.host || "",
    extraction_backend: source?.extractionBackend || "auto",
    extraction_mode: source?.extractionMode || "schema",
    extraction_schema: source?.id
      ? ""
      : JSON.stringify(DEFAULT_SCHEMA, null, 2),
    extraction_prompt: source?.extractionPrompt || "",
    notes: source?.notes || "",
    active: source?.active ?? true,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isEdit) {
      put(`/admin/sources/${source.id}`)
    } else {
      post("/admin/sources")
    }
  }

  const allErrors = Object.entries(errors).flatMap(([field, messages]) =>
    messages.map((msg) => `${field} ${msg}`)
  )

  return (
    <AppLayout title={title}>
      <main className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {allErrors.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <h2 className="font-medium text-red-900">
                {allErrors.length} error(s) prevented this source from saving:
              </h2>
              <ul className="mt-2 list-inside list-disc text-sm text-red-700">
                {allErrors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-4 rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-slate-700"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                required
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            <div>
              <label
                htmlFor="latest_url"
                className="block text-sm font-medium text-slate-700"
              >
                Listing URL
              </label>
              <input
                type="url"
                id="latest_url"
                value={data.latest_url}
                onChange={(e) => setData("latest_url", e.target.value)}
                required
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            <div>
              <label
                htmlFor="host"
                className="block text-sm font-medium text-slate-700"
              >
                Host
              </label>
              <input
                type="text"
                id="host"
                value={data.host}
                onChange={(e) => setData("host", e.target.value)}
                placeholder="www.example.com"
                required
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            <div>
              <label
                htmlFor="extraction_backend"
                className="block text-sm font-medium text-slate-700"
              >
                Extraction Backend
              </label>
              <select
                id="extraction_backend"
                value={data.extraction_backend}
                onChange={(e) =>
                  setData(
                    "extraction_backend",
                    e.target.value as Source["extractionBackend"]
                  )
                }
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              >
                {EXTRACTION_BACKENDS.map((backend) => (
                  <option key={backend} value={backend}>
                    {backend.charAt(0).toUpperCase() + backend.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="extraction_mode"
                className="block text-sm font-medium text-slate-700"
              >
                Extraction Mode
              </label>
              <select
                id="extraction_mode"
                value={data.extraction_mode}
                onChange={(e) =>
                  setData(
                    "extraction_mode",
                    e.target.value as Source["extractionMode"]
                  )
                }
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              >
                {EXTRACTION_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="extraction_schema"
                className="block text-sm font-medium text-slate-700"
              >
                Extraction Schema
              </label>
              <textarea
                id="extraction_schema"
                value={data.extraction_schema}
                onChange={(e) => setData("extraction_schema", e.target.value)}
                rows={10}
                className="mt-1 block w-full rounded-md border-slate-300 font-mono text-sm shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
              <p className="mt-1 text-sm text-slate-500">
                Provide JSON that matches the expected listing structure.
              </p>
            </div>

            <div>
              <label
                htmlFor="extraction_prompt"
                className="block text-sm font-medium text-slate-700"
              >
                Extraction Prompt
              </label>
              <textarea
                id="extraction_prompt"
                value={data.extraction_prompt}
                onChange={(e) => setData("extraction_prompt", e.target.value)}
                rows={6}
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            <div>
              <label
                htmlFor="notes"
                className="block text-sm font-medium text-slate-700"
              >
                Notes
              </label>
              <textarea
                id="notes"
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={data.active}
                onChange={(e) => setData("active", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500"
              />
              <label htmlFor="active" className="text-sm text-slate-700">
                Active
              </label>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={processing}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {processing ? "Saving..." : "Save"}
            </button>
            <Link
              href="/admin/sources"
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Back
            </Link>
          </div>
        </form>
      </main>
    </AppLayout>
  )
}
